// Vessel Tracking Content - Track vessels by name when no container/BL available
import { useState, useEffect } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { API_URL } from "../../config/api";
import { useAuth } from "../../context/AuthContext";
import {
  Ship,
  Search,
  Loader2,
  Plus,
  Trash2,
  MapPin,
  Clock,
  Calendar,
  Anchor,
  Navigation,
  ExternalLink,
  Globe,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Info,
  Flag,
  Radio,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

// Common ports for dropdown
const COMMON_PORTS = [
  { code: "SIKOP", name: "Koper, Slovenia" },
  { code: "SILJU", name: "Ljubljana, Slovenia" },
  { code: "CNSHA", name: "Shanghai, China" },
  { code: "CNYTN", name: "Yantian, China" },
  { code: "CNNGB", name: "Ningbo, China" },
  { code: "HKHKG", name: "Hong Kong" },
  { code: "SGSIN", name: "Singapore" },
  { code: "MYPKG", name: "Port Klang, Malaysia" },
  { code: "LKCMB", name: "Colombo, Sri Lanka" },
  { code: "AEJEA", name: "Jebel Ali, UAE" },
  { code: "EGPSD", name: "Port Said, Egypt" },
  { code: "GRPIR", name: "Piraeus, Greece" },
  { code: "ITGOA", name: "Genoa, Italy" },
  { code: "NLRTM", name: "Rotterdam, Netherlands" },
  { code: "DEHAM", name: "Hamburg, Germany" },
  { code: "BEANR", name: "Antwerp, Belgium" },
  { code: "EGALY", name: "Alexandria, Egypt" },
  { code: "HRJRK", name: "Rijeka, Croatia" },
];

export default function VesselTrackingContent() {
  const { getAuthHeaders } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [serviceType, setServiceType] = useState("direct"); // direct or transhipment
  const [vesselName, setVesselName] = useState("");
  const [originPort, setOriginPort] = useState("");
  const [destinationPort, setDestinationPort] = useState("SIKOP"); // Default to Koper
  const [etd, setEtd] = useState("");
  const [eta, setEta] = useState("");
  
  // For transhipment - multiple legs
  const [legs, setLegs] = useState([
    { id: 1, vesselName: "", fromPort: "", toPort: "", etd: "", eta: "" }
  ]);
  
  // Tracked voyages
  const [trackedVoyages, setTrackedVoyages] = useState([]);
  const [expandedVoyage, setExpandedVoyage] = useState(null);
  
  // Search results (from SeaRates vessel lookup)
  const [searchResult, setSearchResult] = useState(null);

  // Load tracked voyages on mount
  useEffect(() => {
    loadTrackedVoyages();
  }, []);

  const loadTrackedVoyages = async () => {
    try {
      const response = await axios.get(`${API_URL}/tracking/vessels`, {
        headers: getAuthHeaders(),
      });
      setTrackedVoyages(response.data.voyages || []);
    } catch (error) {
      console.error("Error loading voyages:", error);
    }
  };

  const addLeg = () => {
    const lastLeg = legs[legs.length - 1];
    setLegs([
      ...legs,
      {
        id: legs.length + 1,
        vesselName: "",
        fromPort: lastLeg.toPort, // Start from previous leg's destination
        toPort: legs.length === 1 ? destinationPort : "",
        etd: "",
        eta: "",
      },
    ]);
  };

  const removeLeg = (id) => {
    if (legs.length > 1) {
      setLegs(legs.filter((leg) => leg.id !== id));
    }
  };

  const updateLeg = (id, field, value) => {
    setLegs(legs.map((leg) => (leg.id === id ? { ...leg, [field]: value } : leg)));
  };

  const handleSearch = async () => {
    const searchVessel = serviceType === "direct" ? vesselName : legs[0]?.vesselName;
    
    if (!searchVessel) {
      toast.error("Please enter a vessel name");
      return;
    }

    setIsLoading(true);
    try {
      // Try to find vessel info via SeaRates (from container tracking response)
      const response = await axios.post(
        `${API_URL}/tracking/vessel/lookup`,
        { vessel_name: searchVessel },
        { headers: getAuthHeaders() }
      );
      
      if (response.data.vessel) {
        setSearchResult(response.data);
        toast.success(`Found vessel: ${response.data.vessel.name}`);
      } else {
        toast.info("Vessel details not found in our system. You can still create a voyage manually.");
        setSearchResult(null);
      }
    } catch (error) {
      console.error("Vessel lookup error:", error);
      toast.info("Vessel lookup unavailable. Create voyage manually.");
      setSearchResult(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateVoyage = async () => {
    setIsLoading(true);
    try {
      const voyageData = {
        service_type: serviceType,
        vessel_name: serviceType === "direct" ? vesselName : null,
        origin_port: serviceType === "direct" ? originPort : legs[0]?.fromPort,
        destination_port: serviceType === "direct" ? destinationPort : legs[legs.length - 1]?.toPort,
        etd: serviceType === "direct" ? etd : legs[0]?.etd,
        eta: serviceType === "direct" ? eta : legs[legs.length - 1]?.eta,
        legs: serviceType === "transhipment" ? legs : null,
        vessel_info: searchResult?.vessel || null,
      };

      const response = await axios.post(
        `${API_URL}/tracking/vessel/voyage`,
        voyageData,
        { headers: getAuthHeaders() }
      );

      toast.success("Voyage created successfully!");
      loadTrackedVoyages();
      
      // Reset form
      setVesselName("");
      setOriginPort("");
      setEtd("");
      setEta("");
      setLegs([{ id: 1, vesselName: "", fromPort: "", toPort: "", etd: "", eta: "" }]);
      setSearchResult(null);
    } catch (error) {
      console.error("Create voyage error:", error);
      toast.error(error.response?.data?.detail || "Failed to create voyage");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteVoyage = async (voyageId) => {
    try {
      await axios.delete(`${API_URL}/tracking/vessel/voyage/${voyageId}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Voyage deleted");
      loadTrackedVoyages();
    } catch (error) {
      toast.error("Failed to delete voyage");
    }
  };

  const openMarineTraffic = (vessel) => {
    // Open MarineTraffic search for the vessel
    const searchTerm = vessel.imo 
      ? `https://www.marinetraffic.com/en/ais/details/ships/imo:${vessel.imo}`
      : `https://www.marinetraffic.com/en/ais/index/search/all/keyword:${encodeURIComponent(vessel.name || vessel.vessel_name)}`;
    window.open(searchTerm, "_blank");
  };

  const PortSelect = ({ value, onChange, label, placeholder }) => (
    <div className="space-y-1.5">
      <Label className="text-sm text-slate-600">{label}</Label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
      >
        <option value="">{placeholder}</option>
        {COMMON_PORTS.map((port) => (
          <option key={port.code} value={port.code}>
            {port.name}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <div className="max-w-[1400px] mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-xl p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <Ship className="h-8 w-8" />
          <h1 className="text-2xl font-bold">Vessel Tracking</h1>
        </div>
        <p className="text-indigo-100">
          Track vessel voyages when container or B/L numbers are unavailable. Add vessels manually and monitor their progress.
        </p>
      </div>

      {/* Create Voyage Form */}
      <div className="bg-white rounded-xl border shadow-sm p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Plus className="h-5 w-5 text-indigo-600" />
          Create New Voyage
        </h2>

        {/* Service Type Toggle */}
        <div className="mb-6">
          <Label className="text-sm text-slate-600 mb-2 block">Service Type</Label>
          <div className="flex gap-2">
            <Button
              type="button"
              variant={serviceType === "direct" ? "default" : "outline"}
              onClick={() => setServiceType("direct")}
              className="flex-1"
              data-testid="vessel-service-direct"
            >
              <Navigation className="h-4 w-4 mr-2" />
              Direct Service
            </Button>
            <Button
              type="button"
              variant={serviceType === "transhipment" ? "default" : "outline"}
              onClick={() => setServiceType("transhipment")}
              className="flex-1"
              data-testid="vessel-service-transhipment"
            >
              <Anchor className="h-4 w-4 mr-2" />
              Transhipment
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            {serviceType === "direct" 
              ? "Vessel goes directly from origin to destination" 
              : "Cargo transfers to another vessel at transhipment port(s)"}
          </p>
        </div>

        {/* Direct Service Form */}
        {serviceType === "direct" && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-sm text-slate-600">Vessel Name</Label>
                <div className="flex gap-2">
                  <Input
                    value={vesselName}
                    onChange={(e) => setVesselName(e.target.value)}
                    placeholder="e.g., CMA CGM MONTE CRISTO"
                    data-testid="vessel-name-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleSearch}
                    disabled={isLoading}
                    data-testid="vessel-search-btn"
                  >
                    {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <PortSelect
                value={originPort}
                onChange={setOriginPort}
                label="Origin Port"
                placeholder="Select origin..."
              />
              <PortSelect
                value={destinationPort}
                onChange={setDestinationPort}
                label="Destination Port"
                placeholder="Select destination..."
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-sm text-slate-600">ETD (Estimated Departure)</Label>
                <Input
                  type="datetime-local"
                  value={etd}
                  onChange={(e) => setEtd(e.target.value)}
                  data-testid="vessel-etd-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm text-slate-600">ETA (Estimated Arrival)</Label>
                <Input
                  type="datetime-local"
                  value={eta}
                  onChange={(e) => setEta(e.target.value)}
                  data-testid="vessel-eta-input"
                />
              </div>
            </div>
          </div>
        )}

        {/* Transhipment Form - Multiple Legs */}
        {serviceType === "transhipment" && (
          <div className="space-y-4">
            {legs.map((leg, index) => (
              <div key={leg.id} className="border rounded-lg p-4 bg-slate-50">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-slate-900 flex items-center gap-2">
                    <span className="bg-indigo-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">
                      {index + 1}
                    </span>
                    Leg #{index + 1}
                    {index === 0 && <span className="text-xs text-slate-500">(Origin)</span>}
                    {index === legs.length - 1 && legs.length > 1 && (
                      <span className="text-xs text-slate-500">(Final)</span>
                    )}
                  </h3>
                  {legs.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeLeg(leg.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-600">Vessel Name</Label>
                    <Input
                      value={leg.vesselName}
                      onChange={(e) => updateLeg(leg.id, "vesselName", e.target.value)}
                      placeholder="Vessel name"
                      className="h-9 text-sm"
                    />
                  </div>
                  <PortSelect
                    value={leg.fromPort}
                    onChange={(val) => updateLeg(leg.id, "fromPort", val)}
                    label="From Port"
                    placeholder="Select..."
                  />
                  <PortSelect
                    value={leg.toPort}
                    onChange={(val) => updateLeg(leg.id, "toPort", val)}
                    label="To Port (T/S)"
                    placeholder="Select..."
                  />
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-600">ETD</Label>
                    <Input
                      type="datetime-local"
                      value={leg.etd}
                      onChange={(e) => updateLeg(leg.id, "etd", e.target.value)}
                      className="h-9 text-sm"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-600">ETA</Label>
                    <Input
                      type="datetime-local"
                      value={leg.eta}
                      onChange={(e) => updateLeg(leg.id, "eta", e.target.value)}
                      className="h-9 text-sm"
                    />
                  </div>
                </div>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              onClick={addLeg}
              className="w-full"
              data-testid="vessel-add-leg-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Another Leg
            </Button>
          </div>
        )}

        {/* Vessel Info Preview (if found) */}
        {searchResult?.vessel && (
          <div className="mt-4 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-indigo-900 flex items-center gap-2">
                  <Ship className="h-4 w-4" />
                  {searchResult.vessel.name}
                </h4>
                <div className="flex items-center gap-4 mt-1 text-sm text-indigo-700">
                  {searchResult.vessel.imo && <span>IMO: {searchResult.vessel.imo}</span>}
                  {searchResult.vessel.mmsi && <span>MMSI: {searchResult.vessel.mmsi}</span>}
                  {searchResult.vessel.flag && (
                    <span className="flex items-center gap-1">
                      <Flag className="h-3 w-3" />
                      {searchResult.vessel.flag}
                    </span>
                  )}
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => openMarineTraffic(searchResult.vessel)}
                className="text-indigo-700 border-indigo-300"
              >
                <ExternalLink className="h-4 w-4 mr-1" />
                MarineTraffic
              </Button>
            </div>
          </div>
        )}

        {/* Create Button */}
        <div className="mt-6 flex justify-end">
          <Button
            onClick={handleCreateVoyage}
            disabled={isLoading || (!vesselName && !legs[0]?.vesselName)}
            data-testid="vessel-create-voyage-btn"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            Create Voyage
          </Button>
        </div>
      </div>

      {/* Tracked Voyages List */}
      <div className="bg-white rounded-xl border shadow-sm p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Ship className="h-5 w-5 text-indigo-600" />
          Tracked Voyages ({trackedVoyages.length})
        </h2>

        {trackedVoyages.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Ship className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>No voyages tracked yet</p>
            <p className="text-sm">Create a voyage above to start tracking</p>
          </div>
        ) : (
          <div className="space-y-3">
            {trackedVoyages.map((voyage) => (
              <div
                key={voyage.id}
                className="border rounded-lg overflow-hidden hover:border-indigo-300 transition-colors"
              >
                {/* Voyage Header */}
                <div
                  className="p-4 bg-slate-50 cursor-pointer flex items-center justify-between"
                  onClick={() => setExpandedVoyage(expandedVoyage === voyage.id ? null : voyage.id)}
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${
                      voyage.status === "completed" ? "bg-green-100 text-green-600" :
                      voyage.status === "in_transit" ? "bg-blue-100 text-blue-600" :
                      "bg-slate-100 text-slate-600"
                    }`}>
                      <Ship className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-900">
                        {voyage.vessel_name || voyage.legs?.[0]?.vesselName || "Unknown Vessel"}
                      </h3>
                      <p className="text-sm text-slate-500 flex items-center gap-2">
                        <MapPin className="h-3 w-3" />
                        {voyage.origin_port_name || voyage.origin_port}
                        <ArrowRight className="h-3 w-3" />
                        {voyage.destination_port_name || voyage.destination_port}
                        {voyage.service_type === "transhipment" && (
                          <span className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded">
                            {voyage.legs?.length || 0} legs
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {voyage.eta && (
                      <div className="text-right">
                        <p className="text-xs text-slate-500">ETA</p>
                        <p className="text-sm font-medium text-slate-700">
                          {new Date(voyage.eta).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        openMarineTraffic(voyage);
                      }}
                      className="text-indigo-600"
                    >
                      <Globe className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteVoyage(voyage.id);
                      }}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    {expandedVoyage === voyage.id ? (
                      <ChevronUp className="h-5 w-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-slate-400" />
                    )}
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedVoyage === voyage.id && (
                  <div className="p-4 border-t bg-white">
                    {/* Vessel Info */}
                    {voyage.vessel_info && (
                      <div className="mb-4 p-3 bg-indigo-50 rounded-lg">
                        <h4 className="text-sm font-medium text-indigo-900 mb-2">Vessel Details</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          {voyage.vessel_info.imo && (
                            <div>
                              <p className="text-indigo-600">IMO</p>
                              <p className="font-medium">{voyage.vessel_info.imo}</p>
                            </div>
                          )}
                          {voyage.vessel_info.mmsi && (
                            <div>
                              <p className="text-indigo-600">MMSI</p>
                              <p className="font-medium">{voyage.vessel_info.mmsi}</p>
                            </div>
                          )}
                          {voyage.vessel_info.call_sign && (
                            <div>
                              <p className="text-indigo-600">Call Sign</p>
                              <p className="font-medium">{voyage.vessel_info.call_sign}</p>
                            </div>
                          )}
                          {voyage.vessel_info.flag && (
                            <div>
                              <p className="text-indigo-600">Flag</p>
                              <p className="font-medium">{voyage.vessel_info.flag}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Transhipment Legs */}
                    {voyage.service_type === "transhipment" && voyage.legs && (
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-slate-700">Voyage Legs</h4>
                        {voyage.legs.map((leg, idx) => (
                          <div key={idx} className="flex items-center gap-3 p-2 bg-slate-50 rounded">
                            <span className="bg-indigo-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs">
                              {idx + 1}
                            </span>
                            <div className="flex-1">
                              <p className="text-sm font-medium">{leg.vesselName}</p>
                              <p className="text-xs text-slate-500">
                                {leg.fromPort} → {leg.toPort}
                              </p>
                            </div>
                            {leg.eta && (
                              <p className="text-xs text-slate-500">
                                ETA: {new Date(leg.eta).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="mt-4 flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openMarineTraffic(voyage)}
                        className="flex-1"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Live Tracking on MarineTraffic
                      </Button>
                    </div>
                    
                    {/* Info Note */}
                    <div className="mt-3 p-2 bg-amber-50 rounded text-xs text-amber-700 flex items-start gap-2">
                      <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <span>
                        Vessel positions shown are from last known data. Click "Live Tracking" for real-time position on MarineTraffic.
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
